/**
 * Deep-link ancestor expansion for hierarchical scope dimensions.
 *
 * The nav sidebar opens each scope level only when that level's step is
 * present in the path (ScopeSidebar's `isActiveDim` chain): depth 0 lists
 * the roots, the selected entry expands to reveal depth 1, and so on. A
 * deep node reached by a single step therefore can't be revealed — depth 0
 * lists roots, the deep node isn't a root, nothing matches, and the
 * recursion never descends.
 *
 * But that's exactly the shape a deep link lands in. The cluster anatomy
 * map navigates with `[...prefix, { dim: "clusters", id: <leaf> }]`
 * (ClusterAnatomyBody.goToNode) and a shared/permalink URL carries the same
 * single deep step. The deepest step still scopes the data correctly
 * (cluster_subtree resolves the subtree server-side; a file/project step
 * carries its full path), so only the sidebar's orientation is missing.
 *
 * expandHierarchicalScope rewrites such a step into its full ancestor chain
 * (root → … → node) so the existing chain-open reveals the whole hierarchy.
 * It's additive and idempotent: an already-expanded path re-encodes
 * identically, and when ancestors can't be determined confidently (a flat
 * cluster generation, an unknown id, a tree-load failure) the run is left
 * untouched rather than collapsed.
 *
 * Per-dim ancestry:
 *   - clusters: walk the cached cluster tree's parent pointers
 *     (ancestorChain). Async — the tree is fetched once per generation.
 *   - files:    the step id is the full path; ancestors are its `/`-prefixes.
 *   - projects: the step id is `<projectId>:<path>`; ancestors are the bare
 *     project root plus each `/`-prefix of the path.
 */
import { stepIds } from "./dimensionDefs.js";
import { loadClusterTree } from "./clusterTree.js";
import { ancestorChain } from "./clusterIcicle.js";

const HIERARCHICAL_DIMS = new Set(["clusters", "files", "projects"]);

function makeStep(dim, id) {
  const s = String(id);
  return { dim, ids: [s], id: s };
}

// Ancestor ids (root-first, EXCLUDING the node itself) for the path-encoded
// dims. Returns [] for a root node (nothing to expand).
function pathAncestorIds(dim, id) {
  const raw = String(id);
  if (dim === "files") {
    const parts = raw.split("/").filter(Boolean);
    const out = [];
    for (let i = 1; i < parts.length; i++) out.push(parts.slice(0, i).join("/"));
    return out;
  }
  if (dim === "projects") {
    const sep = raw.indexOf(":");
    if (sep < 0) return []; // bare project root — no ancestors
    const projectId = raw.slice(0, sep);
    const path = raw.slice(sep + 1);
    const parts = path.split("/").filter(Boolean);
    const out = [projectId]; // the bare-project root step
    for (let i = 1; i < parts.length; i++) {
      out.push(`${projectId}:${parts.slice(0, i).join("/")}`);
    }
    return out;
  }
  return [];
}

// True when `run` already equals `[...ancestors, deepest]` as single-id
// steps of `dim` — so we can skip the rewrite and keep the original refs.
function runMatches(run, dim, ancestorIds, deepestId) {
  const wantIds = [...ancestorIds, deepestId];
  if (run.length !== wantIds.length) return false;
  for (let k = 0; k < run.length; k++) {
    const ids = stepIds(run[k]);
    if (run[k].dim !== dim || ids.length !== 1 || String(ids[0]) !== String(wantIds[k])) {
      return false;
    }
  }
  return true;
}

/**
 * Expand any deep hierarchical step in `scopePath` into its full ancestor
 * chain. Returns the SAME array reference when nothing changes (so callers
 * can cheaply detect a no-op and avoid a redundant URL write).
 *
 * Async only because cluster ancestry needs the (cached) cluster tree; the
 * tree is fetched lazily and only when a clusters run is present.
 */
export async function expandHierarchicalScope(scopePath) {
  if (!Array.isArray(scopePath) || scopePath.length === 0) return scopePath;

  let clusterTree; // undefined = not yet fetched this call
  const getClusterTree = async () => {
    if (clusterTree === undefined) {
      try {
        clusterTree = await loadClusterTree();
      } catch {
        clusterTree = null;
      }
    }
    return clusterTree;
  };

  const out = [];
  let changed = false;
  let i = 0;
  while (i < scopePath.length) {
    const step = scopePath[i];
    const dim = step?.dim;
    if (!HIERARCHICAL_DIMS.has(dim)) {
      out.push(step);
      i += 1;
      continue;
    }
    // Maximal run of consecutive same-dim steps — a single drill chain.
    let j = i;
    while (j < scopePath.length && scopePath[j]?.dim === dim) j += 1;
    const run = scopePath.slice(i, j);
    i = j;

    // A multi-id step anywhere in the run is a union of nodes, not one
    // path — there's no single ancestor chain to expand to. Leave it.
    const anyMulti = run.some((s) => stepIds(s).length > 1);
    const deepestId = stepIds(run[run.length - 1])[0];
    if (anyMulti || deepestId == null) {
      out.push(...run);
      continue;
    }

    // Only act when we can determine the FULL ancestor chain confidently;
    // otherwise leave the run as-is (never collapse a populated path).
    let ancestorIds = null;
    if (dim === "clusters") {
      const tree = await getClusterTree();
      if (tree && tree.isHierShape && tree.byId.has(String(deepestId))) {
        ancestorIds = ancestorChain(tree, deepestId).map((n) => n.id);
      }
    } else {
      ancestorIds = pathAncestorIds(dim, deepestId);
    }
    if (ancestorIds == null) {
      out.push(...run);
      continue;
    }

    if (runMatches(run, dim, ancestorIds, deepestId)) {
      out.push(...run);
      continue;
    }
    for (const id of ancestorIds) out.push(makeStep(dim, id));
    out.push(makeStep(dim, deepestId));
    changed = true;
  }

  return changed ? out : scopePath;
}
